import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../Button/Button.jsx";
import Banner from "../Banner/Banner.jsx";
import HelpSupportModal from "../HelpSupportModal/HelpSupportModal.jsx";
import { maskEin } from "../../../utils/format.js";
import { openReturnAtStep, openBusinessToFix } from "../../../utils/openReturnAtStep.js";
import { getRejectionErrors } from "../../../services/filingsService.js";

// action (RejectionAction, backend) -> wizard step key (stepConfig.js). Only
// the wizard-step sections are here - GO_TO_BUSINESS is handled separately
// (Business isn't a wizard step) and CONTACT_SUPPORT has no navigation at all.
const ACTION_STEP_KEY = {
  GO_TO_FORM: "form",
  GO_TO_AMENDED_RETURN: "amendedReturn",
  GO_TO_VIN_CORRECTION: "vinCorrection",
  GO_TO_TRUCKS: "trucks",
  GO_TO_PRIOR_YEAR: "priorYear",
  GO_TO_CREDITS: "credits",
  GO_TO_PAY: "pay",
};

const SECTION_LABEL = {
  BUSINESS: "Business",
  FORM: "Form",
  AMENDED_RETURN: "Amended Return",
  VIN_CORRECTION: "VIN Correction",
  // The wizard step is "Trucks" (stepConfig.js) - this said "Vehicles", so the
  // button sent filers to a step name they couldn't find.
  TRUCKS: "Trucks",
  PRIOR_YEAR: "Prior Year",
  CREDITS: "Credits",
  PAY: "Payment Method",
};

// row: a My Filings table row (id, businessName, ein, year, businessId) for a
// REJECTED filing. dispatch/showToast come from the caller's useApp(), same
// prop-passing convention SubscriptionPurchaseModal/PaymentReceiptModal use.
const RejectionErrorModal = ({ row, onClose, dispatch, showToast }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [errors, setErrors] = useState([]);
  const [actingCode, setActingCode] = useState(null);
  const [supportOpen, setSupportOpen] = useState(false);

  useEffect(() => {
    if (!row) return;
    let active = true;
    setLoading(true);
    setLoadFailed(false);
    getRejectionErrors(row.id)
      .then((r) => {
        if (active) setErrors(Array.isArray(r?.errors) ? r.errors : []);
      })
      .catch(() => {
        if (active) setLoadFailed(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [row]);

  if (!row) return null;

  const handleFix = async (err) => {
    setActingCode(err.code || err.title);
    try {
      if (err.action === "GO_TO_BUSINESS") {
        await openBusinessToFix(row, { dispatch, navigate });
      } else {
        const stepKey = ACTION_STEP_KEY[err.action];
        if (!stepKey) {
          setSupportOpen(true);
          return;
        }
        await openReturnAtStep(row, stepKey, { dispatch, navigate });
      }
      onClose();
    } catch (e) {
      showToast(e.message || "Couldn't open this filing. Please try again.");
    } finally {
      setActingCode(null);
    }
  };

  const noDetailAvailable = !loading && (loadFailed || errors.length === 0);
  const hasCorrectable = errors.some((e) => e.correctable);
  const hasSupportRequired = errors.some((e) => !e.correctable);

  return (
    <>
      <div
        className="fixed inset-0 z-[800] flex items-center justify-center p-5"
        style={{ background: "rgba(15,23,42,.5)" }}
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <div className="bg-[var(--panel)] rounded-[20px] w-full max-w-[640px] max-h-[85vh] flex flex-col shadow-[0_24px_64px_rgba(0,0,0,0.3)] overflow-hidden border border-[var(--line)]">
          <div className="py-5 px-6 flex items-start justify-between gap-4 flex-none" style={{ background: "var(--ink)" }}>
            <div className="min-w-0">
              <div className="text-[1.1rem] font-extrabold text-white flex items-center gap-2">
                <span aria-hidden>⚠</span> IRS Rejection
              </div>
              <div className="text-[12.5px] text-white/55 mt-1 break-words">
                Filing #{row.id} · Tax year {row.year || "—"} · {row.businessName || "—"}
                {row.ein && <> · {maskEin(row.ein)}</>}
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="w-[30px] h-[30px] rounded-[8px] bg-[rgba(255,255,255,0.1)] border border-[rgba(255,255,255,0.12)] flex items-center justify-center text-[rgba(255,255,255,0.7)] cursor-pointer hover:bg-[rgba(255,255,255,0.18)] hover:text-white transition-colors flex-none"
            >
              ×
            </button>
          </div>

          <div className="p-6 overflow-y-auto flex-1">
            {loading ? (
              <div className="text-center text-[var(--muted)] py-10">Loading the IRS rejection details…</div>
            ) : noDetailAvailable ? (
              <Banner tone="danger" icon="⚠">
                We couldn't retrieve the detailed IRS rejection reason for this filing. Please contact support with
                your filing ID ({row.id}) so we can investigate.
              </Banner>
            ) : (
              <>
                <p className="text-[14px] font-semibold text-[var(--ink)] mb-1">
                  {errors.length} issue{errors.length === 1 ? "" : "s"} {errors.length === 1 ? "was" : "were"}{" "}
                  returned by the IRS.
                </p>
                {hasCorrectable && hasSupportRequired && (
                  <p className="text-[13px] text-[var(--muted)] mb-4">
                    Some issues can be corrected in your filing, but one or more require assistance from our support
                    team. Please review each issue below - don't assume every issue can be fixed the same way.
                  </p>
                )}

                <div className="flex flex-col gap-3 mt-3">
                  {errors.map((err, i) => (
                    <div key={`${err.code || "unknown"}-${i}`} className="border border-[var(--line-2)] rounded-[var(--r-sm)] p-4">
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div className="text-[14px] font-bold text-[var(--ink)]">
                          {i + 1}. {err.title || "IRS rejection"}
                        </div>
                        <span
                          className={
                            "text-[11px] font-bold py-[2px] px-2 rounded-[20px] whitespace-nowrap flex-none " +
                            (err.correctable
                              ? "bg-[var(--accent-soft)] text-[var(--accent)]"
                              : "bg-[var(--danger-soft)] text-[var(--danger)]")
                          }
                        >
                          {err.correctable ? "You can fix this" : "Needs support"}
                        </span>
                      </div>

                      <div className="text-[12px] text-[var(--muted)] mb-2 font-mono">
                        IRS Code: {err.code || "Unknown"}
                        {err.severity && <span className="ml-2">· {err.severity}</span>}
                      </div>

                      <div className="text-[13px] text-[var(--ink)] bg-[var(--panel-2)] rounded-[8px] p-3 mb-3 max-h-[130px] overflow-y-auto whitespace-pre-line">
                        {err.message || "The IRS did not provide additional message text for this code."}
                      </div>

                      <div className="text-[13px] text-[var(--muted)] mb-2">
                        <b className="text-[var(--ink)]">What this means: </b>
                        {err.explanation}
                      </div>

                      {err.correctable ? (
                        <>
                          {err.howToFix && (
                            <div className="text-[13px] text-[var(--muted)] whitespace-pre-line mb-3">
                              <b className="text-[var(--ink)]">How to fix:</b>
                              {"\n"}
                              {err.howToFix}
                            </div>
                          )}
                          <div className="flex justify-end">
                            <Button size="sm" onClick={() => handleFix(err)} disabled={actingCode === (err.code || err.title)}>
                              {actingCode === (err.code || err.title)
                                ? "Opening…"
                                : `Go to ${SECTION_LABEL[err.section] || "filing"} →`}
                            </Button>
                          </div>
                        </>
                      ) : (
                        <div className="text-[13px] text-[var(--muted)]">
                          This rejection cannot be corrected by changing the information in your filing. Please
                          contact our support team for assistance.
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          <div className="py-4 px-6 border-t border-[var(--line)] flex justify-between gap-3 flex-none">
            <Button variant="ghost" onClick={() => setSupportOpen(true)}>
              ✉ Contact Support
            </Button>
            <Button variant="ghost" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </div>

      <HelpSupportModal open={supportOpen} onClose={() => setSupportOpen(false)} />
    </>
  );
};

export default RejectionErrorModal;
